import type { SortedField } from '@/types'
import transformSortedFieldsToString from '@/helpers/transform-sorted-fields-to-string'
import makeApiClient, { Type } from '@/features/api'
import { z } from "zod";
import { paginationSchema } from "@/features/pagination";
import { assertSchema } from "@/helpers/assert-schema";

type Argument = Partial<{
  filter: string
  pageSize: number
  page: number
  sorting: SortedField[]
}>

const { sendGetRequest } = makeApiClient(Type.Core)

export const schema = z.object({
  pagination: paginationSchema,
  projects: z.array(
    z.object({
      id: z.number().int().gt(0),
      encryptedId: z.string(),
      name: z.string(),
      ownerId: z.string(),
      ownerName: z.string(),
      assigneeId: z.string().nullable(),
      assigneeName: z.string().nullable(),
      organisationId: z.number().int().gt(0),
      organisationName: z.string(),
      flags: z.object({
        isOpen: z.boolean(),
        isClosed: z.boolean(),
        userCanEdit: z.boolean(),
        userCanView: z.boolean(),
        userCanTransfer: z.boolean(),
        userCanClose: z.boolean(),
      }),
      transfers: z.array(
        z.object({
          type: z.enum(['owner', 'assignee']),
          toUserId: z.string(),
          toUserName: z.string(),
          fromUserId: z.string(),
          fromUserName: z.string(),
        })
      )
    })
  )
})

export type PaginatedProjectList = z.infer<typeof schema>

export default async function getPaginatedProjectList({ filter, page, pageSize, sorting } : Argument) {
  const url = new URL('https://example.org')

  if (filter) {
    url.searchParams.set('filter', filter)
  }

  if (sorting && sorting.length > 0) {
    url.searchParams.set('sorting', transformSortedFieldsToString(sorting))
  }

  if (page !== undefined && page > 1) {
    url.searchParams.set('page', page.toString())
  }

  if (pageSize !== undefined && pageSize > 1) {
    url.searchParams.set('pageSize', pageSize.toString())
  }

  const { data: projects, meta: pagination } = await sendGetRequest(`projects${url.search}`)
  const result = { projects, pagination }

  assertSchema(result, schema)

  return result
}